import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/contexts/StoreContext';
import type { AdPlacement, HouseAd, StoreAds } from '@/lib/services/store.service';
import { AnuncioProprio } from './AnuncioProprio';
import { AnuncioGoogle } from './AnuncioGoogle';
import { escolherFonte } from './fonte';
import { useAceiteDeAnuncio } from './useAceiteDeAnuncio';
import { escolherAnuncio } from './rotacao';
import { rotaPermiteAnuncio } from './rotas';
import { registrarExibicao } from './useExibicoes';

/**
 * A campanha escolhida por posição em cada página (pela chave da navegação).
 * Por posição, e não só a lista de campanhas: o layout pode remontar na mesma
 * página (o template carrega depois), e o espaço remontado tem de reusar a
 * escolha dele, não se achar repetido.
 */
const NA_PAGINA = new Map<string, Map<AdPlacement, string>>();
function escolhasDaPagina(chave: string): Map<AdPlacement, string> {
  if (!NA_PAGINA.has(chave)) {
    if (NA_PAGINA.size > 50) NA_PAGINA.clear();
    NA_PAGINA.set(chave, new Map());
  }
  return NA_PAGINA.get(chave)!;
}

/**
 * A decisão do espaço, sem React: o anúncio a desenhar, ou null. A loja já vem
 * decidida pela API (`ads` nulo em loja paga); aqui entram a rota, a posição
 * ligada, o idioma e o que já saiu na página.
 */
export function decidirAnuncio(entrada: {
  ads: StoreAds | null;
  posicao: AdPlacement;
  caminho: string;
  idioma: string;
  naPagina: ReadonlySet<string>;
  sorteio?: () => number;
}): HouseAd | null {
  const { ads, posicao, caminho, idioma, naPagina, sorteio } = entrada;
  if (!ads || !ads.placements.includes(posicao) || !rotaPermiteAnuncio(caminho)) return null;
  return escolherAnuncio(ads.house, posicao, idioma || 'pt', naPagina, sorteio);
}

/** No construtor e no link de prévia o lojista vê onde o anúncio entra, sem anúncio de verdade nem contagem. */
function emEdicao(): boolean {
  if (typeof window === 'undefined') return false;
  const caminho = window.location.pathname;
  return caminho.indexOf('/__construtor') === 0 || caminho.indexOf('/__previa') === 0;
}

interface EspacoDeAnuncioProps {
  posicao: AdPlacement;
  className?: string;
}

/** O que o espaço desenha: o bloco do Google ou um anúncio próprio. */
type Escolha = { fonte: 'google'; bloco: string } | { fonte: 'proprio'; anuncio: HouseAd };

const GOOGLE = 'google';

/**
 * Um espaço de anúncio da loja gratuita. Quem decide se a loja tem anúncio (e
 * se o Google vale nela) é a API (`store.ads`); aqui só entram a rota (lista do
 * que pode), a posição ligada, o idioma e o aceite do jogador para o Google.
 * O Google sem preencher dá lugar ao anúncio próprio. Sem nada, o espaço some.
 */
export function EspacoDeAnuncio({ posicao, className }: EspacoDeAnuncioProps) {
  const { store } = useStore();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const ads = store?.ads ?? null;
  const ligado = !!ads && ads.placements.includes(posicao);
  const edicao = emEdicao();
  const aceitou = useAceiteDeAnuncio(store?.id);
  const [googleVazio, setGoogleVazio] = useState(false);

  const escolha = useMemo<Escolha | null>(() => {
    if (edicao || !ads || !ligado || !rotaPermiteAnuncio(location.pathname)) return null;
    const escolhas = escolhasDaPagina(location.key);
    const bloco = aceitou && !googleVazio ? ads.google?.slots[posicao] : undefined;
    const anterior = escolhas.get(posicao);
    if (anterior === GOOGLE && bloco) return { fonte: 'google', bloco };
    if (anterior && anterior !== GOOGLE) {
      const mesma = ads.house.filter((a) => a.id === anterior)[0];
      if (mesma && decidirAnuncio({ ads: { placements: ads.placements, house: [mesma] }, posicao, caminho: location.pathname, idioma: i18n.language, naPagina: new Set() })) {
        return { fonte: 'proprio', anuncio: mesma };
      }
    }
    // Não repete o que as outras posições já mostram nesta página.
    const outras = new Set<string>();
    escolhas.forEach((id, p) => {
      if (p !== posicao && id !== GOOGLE) outras.add(id);
    });
    const proprio = decidirAnuncio({ ads, posicao, caminho: location.pathname, idioma: i18n.language, naPagina: outras });
    const fonte = escolherFonte({ temGoogle: !!bloco, temProprio: !!proprio, houseShare: ads.houseShare ?? 100 });
    if (fonte === 'google' && bloco) {
      escolhas.set(posicao, GOOGLE);
      return { fonte: 'google', bloco };
    }
    if (fonte === 'proprio' && proprio) {
      escolhas.set(posicao, proprio.id);
      return { fonte: 'proprio', anuncio: proprio };
    }
    escolhas.delete(posicao);
    return null;
    // Um sorteio por página (navegação), não a cada render; muda com o aceite e com o Google vazio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, ligado, edicao, posicao, aceitou, googleVazio]);

  // O Google sem preencher vale só para esta página.
  useEffect(() => setGoogleVazio(false), [location.key]);

  const anuncio = escolha?.fonte === 'proprio' ? escolha.anuncio : null;
  useEffect(() => {
    // O Google conta as dele; aqui só o anúncio próprio.
    if (anuncio && store) registrarExibicao(store.id, anuncio.id, posicao);
  }, [anuncio, store, posicao]);

  if (edicao) {
    return ligado ? (
      <div className={className} data-anuncio-espaco={posicao}>
        <div className="flex min-h-20 items-center justify-center rounded-xl border border-dashed border-border bg-muted/40 p-4 text-center text-xs text-muted-foreground">
          {t('store.ads.placeholder')}
        </div>
      </div>
    ) : null;
  }
  if (!escolha) return null;
  return (
    <div className={className} data-anuncio-espaco={posicao}>
      {escolha.fonte === 'google' && ads?.google ? (
        <AnuncioGoogle
          key={`${location.key}-${escolha.bloco}`}
          cliente={ads.google.client}
          bloco={escolha.bloco}
          posicao={posicao}
          aoNaoPreencher={() => {
            escolhasDaPagina(location.key).delete(posicao);
            setGoogleVazio(true);
          }}
        />
      ) : escolha.fonte === 'proprio' ? (
        <AnuncioProprio anuncio={escolha.anuncio} posicao={posicao} />
      ) : null}
    </div>
  );
}
